import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5ConnectFailure } from '../context/ssm5-connection.context';

import { ConnectToSsm5Card } from './connect-to-ssm5-card';
import { Ssm5Instructions } from './ssm5-instructions';
import { Ssm5RecentConnections } from './ssm5-recent-connections';
import { Ssm5SectionTitle } from './ssm5-section-title';

const CONNECT_COLUMN_WIDTH = 458;

/** Props for {@link Ssm5ConnectColumns}. */
export type Ssm5ConnectColumnsProps = {
    /** The failed attempt the connect card explains, if any. */
    failure?: Ssm5ConnectFailure;
    /** Receives the normalized Cloud Connector ID. */
    onConnect: (cloudConnectorId: string) => void;
};

const useStyles = createUseStyles({
    connectColumn: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        gap: 24,
        width: CONNECT_COLUMN_WIDTH,
    },
    connectSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    instructionsColumn: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 24,
        minWidth: 0,
    },
});

/**
 * The disconnected body of the SSM5 page: the connect card with the recent connections under it,
 * next to the instructions.
 *
 * @param props - {@link Ssm5ConnectColumnsProps}
 * @returns the two page columns
 */
export function Ssm5ConnectColumns({ failure, onConnect }: Ssm5ConnectColumnsProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <>
            <section className={classes.connectColumn}>
                <div className={classes.connectSection}>
                    <Ssm5SectionTitle>{t('ssm5:connect.title')}</Ssm5SectionTitle>
                    <ConnectToSsm5Card failure={failure} onConnect={onConnect} />
                </div>
                <Ssm5RecentConnections />
            </section>
            <section className={classes.instructionsColumn}>
                <Ssm5SectionTitle>{t('ssm5:instructions.title')}</Ssm5SectionTitle>
                <Ssm5Instructions />
            </section>
        </>
    );
}
